import type { ComponentProps, ReactNode } from 'react'

import { InfoIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

type LabelSize = 'sm' | 'default' | 'lg' | 'xl'

const sizeClasses: Record<LabelSize, string> = {
    sm: 'py-1.5 text-sm',
    default: 'py-2 text-base',
    lg: 'py-2.5 text-base',
    xl: 'py-3 text-base'
}

/*
 * GoodUnited / Label. Figma: 10 / Components / Label.
 * Form labels stay at 16px, Roboto / text-base / medium.
 */
function Label({ className, ...props }: ComponentProps<'label'>) {
    return (
        <label
            data-slot='label'
            className={cn('flex items-center gap-2 text-base font-medium text-text-primary select-none peer-disabled:cursor-not-allowed peer-disabled:opacity-50', className)}
            {...props}
        />
    )
}

interface LabelWithIconProps extends ComponentProps<'span'> {
    size?: LabelSize
    icon?: ReactNode
}

/* Label/WithIcon: an inline label that lines up with buttons of the same size. */
function LabelWithIcon({ size = 'default', icon, className, children, ...props }: LabelWithIconProps) {
    return (
        <span
            data-slot='label-with-icon'
            data-size={size}
            className={cn('inline-flex items-center gap-2 font-medium text-text-primary [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-icon-primary', sizeClasses[size], className)}
            {...props}
        >
            {icon ?? <InfoIcon aria-hidden='true' />}
            {children}
        </span>
    )
}

export { Label, LabelWithIcon }
export type { LabelSize }
